import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { Spin } from 'antd';
import { auth } from '../api/firebase';

// Components
import Dashboard from '../pages/Dashboard';
import LandingPage from '../pages/LandingPage';
import PomodoroApp from '../features/pomodoro/PomodoroApp';
import HabitTrackerApp from '../features/habit-tracker/HabitTrackerApp';
import AnalyticsPanel from '../components/AnalyticsPanel';
import Settings from '../components/Settings';
import AppShell from '../components/AppShell';
import AuthGuard from '../components/AuthGuard';

// Auth component for sign-in
const AuthPage = () => {
  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1E90FF10, #87CEEB10)'
    }}>
      <AuthGuard />
    </div>
  );
};

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const [user, loading, error] = useAuthState(auth);
  
  console.log('🔒 ProtectedRoute check:', { 
    user: user ? 'authenticated' : 'not authenticated', 
    loading, 
    error: error ? error.message : 'none',
    pathname: window.location.pathname 
  });

  if (loading) {
    console.log('⏳ Auth loading...');
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        gap: '16px'
      }}>
        <Spin size="large" />
        <div style={{ 
          color: '#1E90FF', 
          fontSize: '16px', 
          fontWeight: 500 
        }}>
          Loading your productivity hub...
        </div>
      </div>
    );
  }

  if (error) {
    console.error('❌ Auth error:', error);
    return <Navigate to="/login" replace />;
  }

  if (!user) {
    console.log('🚫 No user, redirecting to login');
    return <Navigate to="/login" replace />;
  }

  console.log('✅ User authenticated, loading dashboard...');
  return children;
};

const AppRoutes = () => {
  const [user, loading] = useAuthState(auth);
  
  console.log('🔄 AppRoutes rendering, current path:', window.location.pathname);
  console.log('👤 User status:', { authenticated: !!user, loading });
  
  // Show loading while checking authentication
  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        gap: '16px'
      }}>
        <Spin size="large" />
        <div style={{ 
          color: '#1E90FF', 
          fontSize: '16px', 
          fontWeight: 500 
        }}>
          Initializing Progress Craft...
        </div>
      </div>
    );
  }
  
  return (
    <Routes>
      {/* Root route - dashboard for authenticated users, landing page for guests */}
      <Route path="/" element={
        user ? (
          <Navigate to="/dashboard" replace />
        ) : (
          <LandingPage />
        )
      } />
      
      {/* Auth routes */}
      <Route path="/login" element={<AuthPage />} />
      
      {/* Protected app routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <AppShell>
            <Dashboard />
          </AppShell>
        </ProtectedRoute>
      } />
      
      <Route path="/pomodoro" element={
        <ProtectedRoute>
          <AppShell>
            <PomodoroApp />
          </AppShell>
        </ProtectedRoute>
      } />
      
      <Route path="/habits" element={
        <ProtectedRoute>
          <AppShell>
            <HabitTrackerApp />
          </AppShell>
        </ProtectedRoute>
      } />
      
      <Route path="/analytics" element={
        <ProtectedRoute>
          <AppShell>
            <AnalyticsPanel />
          </AppShell>
        </ProtectedRoute>
      } />
      
      <Route path="/settings" element={
        <ProtectedRoute>
          <AppShell>
            <Settings />
          </AppShell>
        </ProtectedRoute>
      } />

      {/* Redirect any unknown routes to root */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default AppRoutes; 